import {put, call, all} from 'redux-saga/effects';
import Question from "../../entities/question";
import * as Actions from "../actions/questionActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type QuestionAction = {
    questionId: string;
    question: Question;
    questions: Question[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneQuestion(action: QuestionAction) {
	const session = tabSession.captureGeneration();
	try {
		const question: Question = yield call(api.findOne, 'question', action.questionId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.QUESTION_FETCH_ONE_SUCCESS, questionId: action.questionId, question, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.QUESTION_FETCH_ONE_FAILURE, questionId: action.questionId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {QuestionAction} action
 */
export function* saveQuestion(action: QuestionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ QUESTION_SAVE saga', action.question?.id);

		if (action.question === undefined || action.question === null) {
			console.error('saveQuestion received a null');
			throw new Error('saveQuestion received a null');
		}

		if (!(action.question instanceof Question)) {
			action.question = new Question(action.question);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.question.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.question);
			const addResult = yield call(idb.add, 'question', action.question);
			if (addResult.isErr()) {
				console.error(`⮂ saveQuestion: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertQuestionSave(action.question, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.QUESTION_SAVE_FAILURE, question: action.question, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.QUESTION_SAVE, question: action.question, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertQuestionSave(action.question, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.QUESTION_SAVE_FAILURE, question: action.question, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {QuestionAction} action
 * @param {Question[]} action.questions
 */
export function* saveManyQuestions(action: QuestionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ QUESTION_SAVE_MANY saga', action.questions.map(question => question.id).join(','));

        if (!Array.isArray(action.questions)) {
            console.error('saveManyQuestions expected an array, received', action.questions);
            throw new Error('saveManyQuestions expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveQuestion), under these queue keys
		const dispatchedAddedOns: number[] = action.questions.map(question => question?.addedOn);
		const revertAll = () => action.questions.map((question, i) =>
			put(Actions.revertQuestionSave(question, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Question[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.questions.length; i++) {
				const question = action.questions[i];
				question.persisted = false;
				ensureOperationId(question);
				toSave.push(question);
			}

			const addResult = yield call(idb.add, 'question', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyQuestions: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.QUESTION_SAVE_MANY_FAILURE, questions: action.questions, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.QUESTION_SAVE_MANY, questions: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.QUESTION_SAVE_MANY_FAILURE, questions: action.questions, status: 500, err});

			console.error(`Error saving ${action.questions.length} questions to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {QuestionAction} action
 */
export function* removeQuestion(action: QuestionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.question === undefined || action.question === null || !(action.question instanceof Question)) {
			console.error('removeQuestion expected type Question, received', action.question);
			reportQueued(false);
			throw new Error('removeQuestion expected type Question');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.question.status = 'D';
		action.question.addedOn = newHRTimestamp();
		action.question.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.question);
		const addResult = yield call(idb.add, 'question', action.question);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeQuestion: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.QUESTION_REMOVE_FAILURE, question: action.question, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.QUESTION_REMOVE, question: action.question});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.QUESTION_REMOVE_FAILURE, question: action.question, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Question.
	*
	* @param {QuestionAction} action
	*/
export function* restoreQuestion(action: QuestionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreQuestion expected getCurrentUserId() to return a value');
        return;
    }

	if (action.question === undefined || action.question === null) {
        console.error('restoreQuestion expected a Question, received null');
        throw new Error('restoreQuestion expected a Question, received null');
    }
    else if (!(action.question instanceof Question)) action.question = new Question(action.question); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.question.status = 'U';
		action.question.addedOn = newHRTimestamp();
		action.question.addedBy = userId;
		action.question.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.question);
		const addResult = yield call(idb.add, 'question', action.question);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreQuestion: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.QUESTION_RESTORE_FAILURE, question: action.question, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.QUESTION_RESTORE, question: action.question});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.QUESTION_RESTORE_FAILURE, question: action.question, status: 500, err});

		console.error('Error restoring from database', err);
	}
}